import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Bell, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../App";
import { pushSupported as supported, subscribePush } from "../lib/push";

const DISMISS_KEY = "savi_push_dismissed";

export const PushPromptCard = () => {
  const { token } = useAuth();
  const [show, setShow] = useState(false);
  const [enabling, setEnabling] = useState(false);

  const subscribe = () => subscribePush(token);

  useEffect(() => {
    if (!supported() || !token) return;

    if (Notification.permission === "granted") {
      // Already allowed — silently keep the subscription in sync
      subscribe().catch(() => {});
      return;
    }
    if (Notification.permission === "default" && !localStorage.getItem(DISMISS_KEY)) {
      setShow(true);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!show) return null;

  const enable = async () => {
    setEnabling(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setShow(false);
        return;
      }
      await subscribe();
      toast.success("Notifications on! We'll keep you posted 🔔");
      setShow(false);
    } catch (error) {
      toast.error("Couldn't enable notifications");
    } finally {
      setEnabling(false);
    }
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setShow(false);
  };

  return (
    <div className="bg-white rounded-2xl p-4 border border-stone-100 mt-4 relative" data-testid="push-prompt-card">
      <button
        onClick={dismiss}
        className="absolute top-3 right-3 text-stone-300 hover:text-stone-500"
        data-testid="push-prompt-dismiss"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3 pr-6">
        <div className="w-10 h-10 rounded-xl bg-[#8C9A7C]/15 flex items-center justify-center flex-shrink-0">
          <Bell className="w-5 h-5 text-[#8C9A7C]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm text-[#1A1A1A]">Turn on notifications</p>
          <p className="text-[#1A1A1A]/60 text-xs mt-1">Get a ping for announcements, rides and community events.</p>
          <Button
            size="sm"
            onClick={enable}
            disabled={enabling}
            className="mt-2 bg-[#8C9A7C] hover:bg-[#7C8A6C] text-white rounded-lg text-xs h-7 px-3"
            data-testid="push-prompt-enable"
          >
            {enabling ? "Enabling..." : "Enable"}
          </Button>
        </div>
      </div>
    </div>
  );
};
